import { Descriptions } from "@/components/v2";
import { type DescriptionsProps } from "antd";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { useState } from "react";
import ModalEmailCorrespondence from "./modal-email-correspondence";
import { ModalSmsCorrespondence } from "./modal-sms-correspondence";
import { useQuery } from "@tanstack/react-query";
import { fetchEmailSentDetailsById } from "@/lib/api/resources/emails";
import { fetchSmsSentDetailsById } from "../../../../lib/api/resources/sms";
import { CorrespondenceItem } from "./correspondence-item";
import { Card } from "@rumbledirect/ui";

interface CorrespondenceCardProps {
  dossier: Dossier | undefined;
  isLoading?: boolean;
}

export const CorrespondenceCard = ({
  dossier,
  isLoading,
}: CorrespondenceCardProps) => {
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [isSmsModalOpen, setSmsModalOpen] = useState(false);
  const [selectedCorrespondence, setSelectedCorrespondence] = useState<{
    id: string;
    type: "email" | "sms";
  } | null>(null);

  const emailSentQuery = useQuery({
    queryKey: ["emailSentDetails", selectedCorrespondence?.id],
    queryFn: async () => {
      if (
        !selectedCorrespondence?.id ||
        selectedCorrespondence?.type !== "email"
      )
        return null;
      return fetchEmailSentDetailsById(selectedCorrespondence?.id);
    },
  });

  const smsSentQuery = useQuery({
    queryKey: ["smsSentDetails", selectedCorrespondence?.id],
    queryFn: async () => {
      if (!selectedCorrespondence?.id || selectedCorrespondence?.type !== "sms")
        return null;
      return fetchSmsSentDetailsById(selectedCorrespondence?.id);
    },
  });

  const openCorrespondenceModal = (id: string, type: "email" | "sms") => {
    setSelectedCorrespondence({ id, type });
    if (type === "email") {
      setEmailModalOpen(true);
    } else if (type === "sms") {
      setSmsModalOpen(true);
    }
  };

  const correspondenceItems = [
    {
      label: "E-mail naar hersteller",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageEmailSentServicePartner}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    ...(dossier?.dealershipCompany
      ? [
          {
            label: "E-mail naar dealerbedrijf",
            children: (
              <CorrespondenceItem
                dossierUsageItems={dossier?.dossierUsageEmailSentDealership}
                dossierUsageType="email"
                emptyStateText="Geen e-mails verzonden"
                openCorrespondenceModal={openCorrespondenceModal}
              />
            ),
          },
        ]
      : []),
    {
      label: "E-mail naar bestuurder",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageEmailSentDriver}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "E-mail naar overige",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageEmailSentAffiliate}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "SMS naar klant",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageSmsSentDriver}
          dossierUsageType="sms"
          emptyStateText="Geen SMS verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "Backoffice SHB (DW Pro)",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageDwProSent}
          dossierUsageType="DwPro"
          emptyStateText="Niet verzonden naar DW Pro"
        />
      ),
    },
    {
      label: "ImageCapture naar berijder",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.imageCaptureSents}
          dossierUsageType="ImageCapture"
          emptyStateText="Geen ImageCapture verzonden"
        />
      ),
    },
  ];

  const items: DescriptionsProps["items"] = correspondenceItems.map(
    (item, index) => ({
      key: index,
      label: item.label,
      children: item.children ?? null,
    }),
  );

  return (
    <Card loading={isLoading} className="w-full" title="Correspondentie">
      <Descriptions items={items} />
      <ModalEmailCorrespondence
        title="Inhoud van e-mail"
        emailDetails={emailSentQuery.data}
        isOpen={isEmailModalOpen}
        setIsOpen={setEmailModalOpen}
        loading={emailSentQuery.isLoading}
      />
      <ModalSmsCorrespondence
        title="Inhoud van sms"
        smsDetails={smsSentQuery.data}
        isOpen={isSmsModalOpen}
        setIsOpen={setSmsModalOpen}
        loading={smsSentQuery.isLoading}
      />
    </Card>
  );
};
